import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import { getCategory, categories } from "@/mocks/categories";
import { byCat } from "@/mocks/items";
import { useSeo } from "@/hooks/useSeo";

const sortOpts = [
  { key: "hot", label: "最热", icon: "ri-fire-line" },
  { key: "new", label: "最新", icon: "ri-time-line" },
  { key: "comment", label: "讨论多", icon: "ri-chat-1-line" },
];

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const cat = getCategory(slug);
  const [sortKey, setSortKey] = useState("hot");

  const list = useMemo(() => {
    const src = byCat(slug);
    if (sortKey === "new") return [...src].reverse();
    if (sortKey === "comment") return [...src].sort((a, b) => b.comments - a.comments);
    return [...src].sort((a, b) => b.heat - a.heat);
  }, [slug, sortKey]);

  useSeo({
    title: `${cat ? cat.name : "分类"} - ${cat?.en || ""} | 导航爆料`,
    description: cat
      ? `导航爆料${cat.name}栏目：${cat.tagline}。${cat.blurb}`
      : "导航爆料分类页",
    keywords: `导航爆料,${cat ? cat.name : ""},${cat ? cat.tagline : ""},分类浏览`,
    path: `/category/${slug}`,
    jsonLd: cat
      ? JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `导航爆料 - ${cat.name}`,
          description: `${cat.tagline}。${cat.blurb}`,
          inLanguage: "zh-CN",
          url: `https://daohangbaoliao.example.com/category/${slug}`,
          mainEntity: { "@type": "ItemList" },
        })
      : undefined,
  });

  if (!cat) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
          <i className="ri-question-line"></i>
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这个分类</h1>
        <p className="mt-2 text-sm text-foreground-600">分类可能不存在或已被调整。</p>
        <Link
          to="/"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold whitespace-nowrap text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb
        crumbs={[{ label: "首页", to: "/" }, { label: cat.name }]}
      />
      <section className="mx-auto w-full max-w-7xl px-4 md:px-6">
        {/* 分类头部 */}
        <div className="mt-4 flex flex-col gap-6 overflow-hidden rounded-lg border border-background-200/70 bg-background-100 p-5 md:flex-row md:p-0 md:pr-7">
          <div className="md:w-[38%] md:shrink-0">
            <img
              src={cat.cover}
              alt={`${cat.name}分类封面`}
              className="h-full min-h-[160px] w-full object-cover"
            />
          </div>
          <div className="flex flex-1 flex-col justify-center md:py-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-700">
                <i className={cat.icon}></i>
              </span>
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {cat.name}
              </h1>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800">
                {cat.stat} 条内容
              </span>
            </div>
            <p className="mt-1 text-xs font-semibold tracking-widest text-primary-600 uppercase">{cat.en}</p>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">{cat.tagline}。{cat.blurb}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.filter((c) => c.slug !== cat.slug).map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-background-200 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-50 hover:text-primary-700"
                >
                  <i className={c.icon}></i>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* 排序 + 列表 */}
        <div className="mt-8 flex items-center gap-2 border-b border-background-200 pb-4">
          <span className="text-sm text-foreground-600">排序：</span>
          <div className="inline-flex gap-1 rounded-full bg-background-100 p-1">
            {sortOpts.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setSortKey(o.key)}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                  sortKey === o.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                <i className={o.icon}></i>
                {o.label}
              </button>
            ))}
          </div>
          <span className="ml-auto hidden text-xs text-foreground-400 sm:block">
            共 {list.length} 条
          </span>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      </section>
    </>
  );
}